import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Tone from 'tone';
import {
  attackLessonNotes,
  releaseLessonNotes,
  cancelLessonNotes,
  isPianoReady,
  pianoReady,
  warmUpPiano,
} from '../utils/notePiano';
import { traceSchedule, traceFrames, traceSkip } from '../utils/lessonPerfTrace';

export const LESSON_SPEEDS = [0.5, 0.75, 1];

// Horloge et programmation (point 1 du plan d'optimisation des leçons).
//
// Une seule position musicale, ancrée sur l'horloge audio brute
// (`rawContext.currentTime`). Ni `performance.now()`, ni `Tone.now()` qui
// ajoute le lookAhead de 0,2 s posé sur le contexte partagé.
//
// Les pas sont soumis à des dates audio absolues par un tick de programmation,
// indépendant de ce qui est affiché. Il a deux sources, et il est idempotent :
// il ne soumet que ce qui entre dans la fenêtre d'anticipation.
// - `Context.setInterval`, servi par le ticker de Tone (un worker) : la période
//   demandée est de 25 ms mais la cadence réelle est celle du ticker,
//   `updateInterval` = lookAhead / 2, soit 100 ms avec le lookAhead de 0,2 s
//   posé par synthPlayer sur le contexte partagé. C'est la source qui survit à
//   un onglet masqué, là où requestAnimationFrame tombe à 1 Hz.
// - la boucle de rendu, au début de chaque image : granularité de 16 ms tant
//   que l'onglet est visible.
// Avec une fenêtre de 250 ms, chaque pas part entre 250 et 150 ms avant son
// échéance : un top manqué est absorbé, deux ne le sont pas.
const SCHEDULER_INTERVAL = 0.025;
const SCHEDULE_AHEAD = 0.25;
// Fenêtre des relâches, bien plus courte que celle des attaques : une note posée
// par `triggerRelease` sort du suivi du sampler, donc elle n'est plus annulable.
// 0,12 s = une période du ticker de Tone (0,1 s avec le lookAhead de 0,2 s) plus
// une marge. Une note reste donc annulable jusqu'à environ 120 ms avant sa
// relâche, au lieu de 250 ms si les deux fenêtres étaient confondues.
const RELEASE_AHEAD = 0.12;
// Au-delà de ce retard, un pas est sauté plutôt que déclenché : jamais de
// rafale d'anciens pas après un blocage.
const LATE_TOLERANCE = 0.03;
// Petite avance donnée à un pas rattrapé dans la tolérance.
const LATE_NUDGE = 0.005;
// Marge du geste manuel sur le web. À adapter par plateforme si besoin, sans
// toucher au lookAhead global du contexte partagé.
export const MANUAL_MARGIN = 0.03;

// Garde-temps commun au démarrage du contexte et au chargement du piano : sans
// lui, un contexte qui ne reprend jamais laisserait le bouton en attente sans fin.
const AUDIO_TIMEOUT_MS = 15000;

const DEFAULT_TEMPO = 120;
const EPSILON = 0.001;
const MIN_STEP_DURATION = 0.2;
const MIN_LOOP_DURATION = 0.05;
// Garde-fou données : un dernier pas de section à durée nulle ferait finir la
// boucle sur l'attaque de ce pas, donc deux accords empilés au raccord.
const MIN_LAST_STEP_BEATS = 0.25;
// Garde-fou : le rattrapage avance pas à pas, il ne doit jamais boucler sans fin.
const MAX_STEPS_PER_TICK = 10000;

const noteName = (midi) => Tone.Frequency(midi, 'midi').toNote();

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const hasSteps = (lesson) => Boolean(lesson && Array.isArray(lesson.steps) && lesson.steps.length);

// Horloge audio brute, seule référence du moteur. Repli sur Tone.now() quand le
// contexte n'est pas exposé (mocks de test, environnement sans WebAudio).
const audioTime = () => {
  try {
    const ctx = typeof Tone.getContext === 'function' ? Tone.getContext() : null;
    const raw = ctx && ctx.rawContext;
    if (raw && Number.isFinite(raw.currentTime)) return raw.currentTime;
  } catch (e) {
    // contexte audio indisponible
  }
  return Tone.now();
};

// Le contexte peut être 'suspended' (jamais démarré) ou 'interrupted' (iOS après
// un passage en arrière-plan) : dans les deux cas il faut attendre Tone.start().
// Sans contexte exposé (mocks), on suppose qu'il tourne.
const contextRunning = () => {
  try {
    const ctx = typeof Tone.getContext === 'function' ? Tone.getContext() : null;
    if (!ctx || ctx.state === undefined) return true;
    return ctx.state === 'running';
  } catch (e) {
    return true;
  }
};

// Tone expose setInterval/clearInterval sur son contexte : c'est son ticker
// (un worker par défaut), pas le Transport global, et il survit à un onglet
// masqué là où requestAnimationFrame tombe à 1 Hz.
const startTicker = (fn) => {
  try {
    const ctx = typeof Tone.getContext === 'function' ? Tone.getContext() : null;
    if (ctx && typeof ctx.setInterval === 'function') {
      return { ctx, id: ctx.setInterval(fn, SCHEDULER_INTERVAL) };
    }
  } catch (e) {
    // contexte audio indisponible
  }
  return { ctx: null, id: setInterval(fn, SCHEDULER_INTERVAL * 1000) };
};

const stopTicker = (ticker) => {
  if (!ticker) return;
  if (ticker.ctx) {
    try {
      ticker.ctx.clearInterval(ticker.id);
    } catch (e) {
      // contexte déjà fermé
    }
    return;
  }
  clearInterval(ticker.id);
};

// `Tone.start()` peut ne jamais résoudre (contexte bloqué par la plateforme) :
// on borne l'attente et on rend un code exploitable par l'interface.
const startAudioContext = () => new Promise((resolve, reject) => {
  const timer = setTimeout(() => {
    const err = new Error('audio-context-blocked');
    err.code = 'audio-context-blocked';
    reject(err);
  }, AUDIO_TIMEOUT_MS);
  Promise.resolve(Tone.start()).then(
    () => { clearTimeout(timer); resolve(); },
    (e) => { clearTimeout(timer); reject(e); }
  );
});

const sectionRange = (lesson, stepIndex) => {
  const steps = lesson.steps;
  const whole = { first: 0, last: steps.length - 1 };
  const step = steps[stepIndex];
  if (!step || !Array.isArray(lesson.sections)) return whole;
  const section = lesson.sections[step.sectionIndex];
  if (!section) return whole;
  const first = clamp(section.firstStep, 0, steps.length - 1);
  const last = clamp(section.lastStep, first, steps.length - 1);
  return { first, last };
};

const loopBounds = (lesson, range) => {
  const steps = lesson.steps;
  const lastStep = steps[range.last];
  return {
    ...range,
    start: steps[range.first].time,
    end: lastStep.time + Math.max(lastStep.duration, MIN_LAST_STEP_BEATS),
  };
};

const loopLength = (bounds) => bounds.end - bounds.start;

// La position du moteur est monotone : elle continue au-delà de la fin de
// section. Seul l'affichage la replie dans la boucle.
const foldPos = (pos, bounds) => {
  const len = loopLength(bounds);
  if (!(len > 0)) return pos;
  const offset = (pos - bounds.start) % len;
  return bounds.start + (offset < 0 ? offset + len : offset);
};

const findStepIndex = (steps, first, last, pos) => {
  let idx = first;
  let lo = first;
  let hi = last;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (steps[mid].time <= pos + EPSILON) {
      idx = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  return idx;
};

// Curseur de programmation : le prochain pas à soumettre, avec le numéro de
// boucle. Il franchit le retour de boucle, donc le pas 0 de la boucle suivante
// part en avance comme les autres.
// `inclusive` : le pas pile sur la position compte-t-il ? Oui après un seek ou
// un play (on veut jouer le pas visé), non après une annulation en lecture (le
// pas sur la position est déjà passé, le curseur repart du premier pas non dû).
const cursorFromPos = (steps, bounds, pos, inclusive = true) => {
  const len = loopLength(bounds);
  let loopN = 0;
  let folded = pos;
  if (len > 0) {
    loopN = Math.max(0, Math.floor((pos - bounds.start) / len));
    folded = pos - loopN * len;
  }
  const threshold = inclusive ? folded - EPSILON : folded + EPSILON;
  for (let i = bounds.first; i <= bounds.last; i += 1) {
    if (steps[i].time >= threshold) return { idx: i, loopN };
  }
  // Au-delà du dernier pas : le prochain est le premier de la boucle suivante.
  return { idx: bounds.first, loopN: len > 0 ? loopN + 1 : loopN };
};

const nextCursor = (cursor, bounds) => (cursor.idx >= bounds.last
  ? { idx: bounds.first, loopN: cursor.loopN + 1 }
  : { idx: cursor.idx + 1, loopN: cursor.loopN });

export function useLessonEngine(lesson, { tempo, onStepChange } = {}) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [atSectionStart, setAtSectionStart] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeedState] = useState(1);
  // Disponibilité du piano. `pendingPlay` : un play attend que les samples
  // soient là. Les pas sautés restent dans une ref : en état React, chaque tick
  // en retard re-rendrait tout le lecteur.
  const [pendingPlay, setPendingPlay] = useState(false);
  const [pianoState, setPianoState] = useState('loading');

  const lessonRef = useRef(lesson);
  const tempoRef = useRef(DEFAULT_TEMPO);
  const speedRef = useRef(1);
  const stepIdxRef = useRef(0);
  const cursorRef = useRef({ idx: 0, loopN: 0 });
  const cursorDirtyRef = useRef(true);
  const cursorInclusiveRef = useRef(true);
  // Relâches à poser, triées par date. Chacune garde aussi sa date musicale
  // (`beats`) et son plancher en secondes (`floorAt`, ancré sur l'attaque) :
  // un changement de vitesse les recale sur la nouvelle grille pour que la note
  // garde sa durée écrite. Voir la règle « relâches d'abord » dans le tick.
  const pendingReleasesRef = useRef([]);
  // Dernier pas soumis au sampler : sa date musicale et sa date audio. C'est
  // l'ancre d'un changement de vitesse tant que cette date est à venir.
  const lastScheduledRef = useRef(null);
  // Segments de grille laissés derrière par un changement de vitesse, dans
  // l'ordre. Chacun vaut jusqu'à `until`, la date de l'ancre suivante : les pas
  // déjà soumis sonnent sur l'ancienne grille, l'affichage doit les y suivre.
  const pastGridsRef = useRef([]);
  const pendingPlayRef = useRef(false);
  // Génération du play en attente : un pause/stop/changement de leçon l'incrémente
  // et la promesse qui se résout sur une génération périmée ne démarre rien.
  const playGenRef = useRef(0);
  const skippedRef = useRef(0);
  const onStepChangeRef = useRef(onStepChange);
  // baseBeats : position musicale de l'ancre. anchorAudio : sa date sur
  // l'horloge audio brute. Tout le reste s'en déduit.
  const clockRef = useRef({ baseBeats: 0, anchorAudio: 0, playing: false });
  const atSectionStartRef = useRef(true);

  const setSectionStart = useCallback((value) => {
    atSectionStartRef.current = value;
    setAtSectionStart(value);
  }, []);

  useEffect(() => { onStepChangeRef.current = onStepChange; }, [onStepChange]);

  const beatsPerSecond = useCallback(
    () => (tempoRef.current / 60) * speedRef.current,
    []
  );

  const getPos = useCallback(() => {
    const c = clockRef.current;
    if (!c.playing) return c.baseBeats;
    const t = audioTime();
    // Position par segments : après un changement de vitesse, l'ancienne grille
    // reste vraie jusqu'à l'ancre de la nouvelle. Sans ça, l'affichage passerait
    // tout de suite à la nouvelle grille alors que les pas déjà soumis sonnent
    // encore sur l'ancienne. Les segments dépassés sont oubliés ici.
    const past = pastGridsRef.current;
    while (past.length && t >= past[0].until) past.shift();
    if (past.length) {
      const g = past[0];
      return g.baseBeats + Math.max(0, t - g.anchorAudio) * g.bps;
    }
    // Le départ pose l'ancre une marge dans le futur : la position ne recule
    // jamais avant elle, sinon le repli renverrait la fin de la boucle.
    return c.baseBeats + Math.max(0, t - c.anchorAudio) * beatsPerSecond();
  }, [beatsPerSecond]);

  const resetSkipped = useCallback(() => {
    skippedRef.current = 0;
  }, []);

  const getSkippedSteps = useCallback(() => skippedRef.current, []);

  const markCursor = useCallback((inclusive) => {
    cursorDirtyRef.current = true;
    cursorInclusiveRef.current = inclusive;
  }, []);

  // File des relâches, triée par date : l'ordre des appels compte autant que les
  // dates, `triggerRelease` vidant la liste des sources suivies de la hauteur.
  const pushRelease = useCallback((name, at, beats, floorAt) => {
    const pending = pendingReleasesRef.current;
    let i = pending.length;
    while (i > 0 && pending[i - 1].at > at) i -= 1;
    pending.splice(i, 0, { name, at, beats, floorAt });
  }, []);

  const flushReleases = useCallback((until) => {
    const pending = pendingReleasesRef.current;
    if (!pending.length) return;
    let count = 0;
    while (count < pending.length && pending[count].at <= until + EPSILON) count += 1;
    if (!count) return;
    const due = pending.splice(0, count);
    for (const r of due) releaseLessonNotes([r.name], r.at);
  }, []);

  // Annulation : coupe tout ce qui est parti ou soumis, oublie les relâches en
  // attente, et fait repartir le curseur du premier pas non dû.
  const cancelScheduled = useCallback(() => {
    cancelLessonNotes(audioTime());
    pendingReleasesRef.current = [];
    lastScheduledRef.current = null;
    pastGridsRef.current = [];
    markCursor(false);
  }, [markCursor]);

  // Abandon d'un play en attente du piano : la promesse en vol ne démarrera rien.
  const abandonPending = useCallback(() => {
    playGenRef.current += 1;
    if (!pendingPlayRef.current) return;
    pendingPlayRef.current = false;
    setPendingPlay(false);
  }, []);

  // Changement de vitesse ou de tempo, chemin unique. On n'annule et on ne
  // relâche rien : le Sampler ne sait pas annuler une attaque future toute
  // seule, et `releaseAll` couperait aussi les notes qui sonnent. Les pas déjà
  // soumis partent donc à leur date, sur l'ancienne grille ; la nouvelle prend
  // l'ancre du dernier pas soumis encore à venir, donc elle s'entend au plus
  // tard une fenêtre d'anticipation après le geste. `applyRate` pose la
  // nouvelle valeur entre l'ancienne grille et la nouvelle.
  const retime = useCallback((applyRate) => {
    const c = clockRef.current;
    if (!c.playing) {
      c.baseBeats = getPos();
      applyRate();
      c.anchorAudio = audioTime();
      return;
    }
    const oldBps = beatsPerSecond();
    const now = audioTime();
    const last = lastScheduledRef.current;
    const ahead = last && last.at > now;
    const anchorAudio = ahead ? last.at : now;
    const baseBeats = ahead ? last.beats : getPos();
    // L'ancre ne recule jamais d'un changement à l'autre : entre deux, le
    // dernier pas soumis ne peut qu'avancer. Le segment empilé prolonge donc la
    // liste, que `getPos` purge à la lecture.
    pastGridsRef.current.push({
      baseBeats: c.baseBeats,
      anchorAudio: c.anchorAudio,
      bps: oldBps,
      until: anchorAudio,
    });
    c.baseBeats = baseBeats;
    c.anchorAudio = anchorAudio;
    applyRate();
    const bps = beatsPerSecond();
    if (!(bps > 0)) return;
    // Relâches en attente postérieures à l'ancre : recalées sur la nouvelle
    // grille, sinon la note perdrait sa durée écrite. En accélérant, une relâche
    // restée sur l'ancienne grille tomberait après la ré-attaque de la même
    // hauteur et couperait la nouvelle note. Le plancher reste en secondes,
    // ancré sur l'attaque, qui elle est déjà partie.
    const pending = pendingReleasesRef.current;
    let moved = false;
    for (const r of pending) {
      if (r.at <= anchorAudio) continue;
      r.at = Math.max(anchorAudio + (r.beats - baseBeats) / bps, r.floorAt);
      moved = true;
    }
    if (moved) pending.sort((a, b) => a.at - b.at);
  }, [getPos, beatsPerSecond]);

  // Le saut pose l'ancre une marge dans le futur, comme le départ de la lecture :
  // sans elle, la première note d'un saut en lecture partirait sans marge.
  const seek = useCallback((beat) => {
    const c = clockRef.current;
    c.baseBeats = beat;
    c.anchorAudio = audioTime() + MANUAL_MARGIN;
    markCursor(true);
    resetSkipped();
  }, [resetSkipped, markCursor]);

  const setCurrentStep = useCallback((index, force = false) => {
    if (!force && index === stepIdxRef.current) return;
    stepIdxRef.current = index;
    setCurrentStepIndex(index);
    if (onStepChangeRef.current) onStepChangeRef.current(index);
  }, []);

  const pauseClock = useCallback(() => {
    abandonPending();
    const c = clockRef.current;
    if (!c.playing) return;
    c.baseBeats = getPos();
    c.playing = false;
    cancelScheduled();
    setPlaying(false);
  }, [getPos, abandonPending, cancelScheduled]);

  const scheduleStep = useCallback((step, when, due, beats, bps, stepIndex, audioNow, dueIn) => {
    // Piano pas prêt : on sort avant de toucher à la file, sinon des relâches
    // seraient retirées alors que l'attaque va échouer et que le pas sera rejoué.
    if (!isPianoReady()) return false;
    // Relâches d'abord, jusqu'à la date de cette attaque : un tick peut soumettre
    // plusieurs pas, et une relâche posée après l'attaque du pas suivant
    // couperait la note qui vient de partir sur la même hauteur.
    flushReleases(when);
    const names = step.notes.map((n) => noteName(n.midi));
    if (!attackLessonNotes(names, when)) return false;
    lastScheduledRef.current = { beats, at: due };
    step.notes.forEach((n, i) => {
      // Ancrée sur l'échéance, pas sur `when` : un pas rattrapé part un peu plus
      // tard, sa relâche ne doit pas déborder sur l'attaque du pas suivant.
      const floorAt = due + MIN_LOOP_DURATION;
      pushRelease(
        names[i],
        Math.max(due + n.duration / bps, floorAt),
        beats + n.duration,
        floorAt
      );
    });
    traceSchedule({ stepIndex, musicalTime: step.time, when, audioNow, bps, mode: 'auto', dueIn });
    return true;
  }, [flushReleases, pushRelease]);

  // Programmation : hors de la boucle de rendu, sur dates audio absolues.
  const tick = useCallback(() => {
    const l = lessonRef.current;
    const c = clockRef.current;
    if (!c.playing || !hasSteps(l)) return;
    const steps = l.steps;
    const bounds = loopBounds(l, sectionRange(l, stepIdxRef.current));
    const len = loopLength(bounds);
    if (!(len > 0)) return;
    const bps = beatsPerSecond();
    if (!(bps > 0)) return;
    const pos = getPos();
    if (cursorDirtyRef.current
        || cursorRef.current.idx < bounds.first
        || cursorRef.current.idx > bounds.last) {
      cursorRef.current = cursorFromPos(steps, bounds, pos, cursorInclusiveRef.current);
      cursorDirtyRef.current = false;
      cursorInclusiveRef.current = true;
    }
    const now = audioTime();
    const horizon = now + SCHEDULE_AHEAD;
    // Règle « relâches d'abord » : tout ce qui est dû dans la fenêtre de relâche
    // part avant la moindre attaque. Limite assumée : une même hauteur tenue
    // par-dessus sa propre ré-attaque est coupée à la relâche de la première,
    // comme un piano dont on refrappe la touche.
    flushReleases(now + RELEASE_AHEAD);
    let skipped = 0;
    for (let guard = 0; guard < MAX_STEPS_PER_TICK; guard += 1) {
      const cursor = cursorRef.current;
      const step = steps[cursor.idx];
      // Position musicale monotone du pas : elle sert aussi d'ancre au prochain
      // changement de vitesse, qui ne connaît pas les bornes de boucle.
      const beats = step.time + cursor.loopN * len;
      const due = c.anchorAudio + (beats - c.baseBeats) / bps;
      if (due >= horizon) break;
      const lateBy = now - due;
      if (lateBy > LATE_TOLERANCE) {
        traceSkip({ stepIndex: cursor.idx, lateBy });
        skipped += 1;
        cursorRef.current = nextCursor(cursor, bounds);
        continue;
      }
      // dueIn reste l'échéance non bornée : un pas rattrapé garde son retard
      // visible dans la trace, même si `when` le pousse juste devant `now`.
      if (!scheduleStep(step, lateBy > 0 ? now + LATE_NUDGE : due, due, beats, bps, cursor.idx, now, due - now)) {
        // sampler pas encore prêt : on réessaiera au tick suivant
        break;
      }
      cursorRef.current = nextCursor(cursor, bounds);
    }
    if (skipped) skippedRef.current += skipped;
  }, [beatsPerSecond, getPos, scheduleStep, flushReleases]);

  // Affichage : le pas dont l'attaque est atteinte sur l'horloge audio, jamais
  // celui qu'on vient de soumettre. L'image sert aussi de source au tick.
  const frame = useCallback(() => {
    const l = lessonRef.current;
    const c = clockRef.current;
    if (!c.playing || !hasSteps(l)) return;
    tick();
    const bounds = loopBounds(l, sectionRange(l, stepIdxRef.current));
    const pos = foldPos(getPos(), bounds);
    setCurrentStep(findStepIndex(l.steps, bounds.first, bounds.last, pos));
  }, [tick, getPos, setCurrentStep]);

  useEffect(() => {
    lessonRef.current = lesson;
    // Remplacement de la leçon (sortie, transposition) : ce qui était soumis
    // n'a plus de sens, on coupe avant de reposer l'horloge.
    abandonPending();
    if (clockRef.current.playing) cancelScheduled();
    pendingReleasesRef.current = [];
    lastScheduledRef.current = null;
    pastGridsRef.current = [];
    clockRef.current = {
      baseBeats: hasSteps(lesson) ? lesson.steps[0].time : 0,
      anchorAudio: audioTime(),
      playing: false,
    };
    cursorRef.current = { idx: 0, loopN: 0 };
    markCursor(true);
    skippedRef.current = 0;
    setPlaying(false);
    setSectionStart(true);
    setCurrentStep(0, Boolean(lesson));
    if (lesson) warmUpPiano();
  }, [lesson, setCurrentStep, setSectionStart, abandonPending, cancelScheduled, markCursor]);

  useEffect(() => {
    if (!playing) return undefined;
    let rafId;
    const loop = () => {
      frame();
      if (clockRef.current.playing) rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    const ticker = startTicker(tick);
    traceFrames(true);
    return () => {
      cancelAnimationFrame(rafId);
      stopTicker(ticker);
      // Plus personne ne servira les relâches en attente : on coupe.
      cancelScheduled();
      traceFrames(false);
    };
  }, [playing, frame, tick, cancelScheduled]);

  // Démontage : la promesse d'un play en attente ne doit rien démarrer après coup.
  useEffect(() => () => { playGenRef.current += 1; }, []);

  // Ancrage du départ. Séparé de `play` : il sert aussi au départ différé, une
  // fois le piano prêt, et l'ancre est alors posée à cet instant-là.
  const startClock = useCallback(() => {
    const l = lessonRef.current;
    if (!hasSteps(l)) return;
    const c = clockRef.current;
    if (c.playing) return;
    setSectionStart(false);
    const step = l.steps[clamp(stepIdxRef.current, 0, l.steps.length - 1)];
    c.baseBeats = step.time;
    // Le premier pas part à la même marge qu'un geste manuel : son et image
    // arrivent ensemble.
    c.anchorAudio = audioTime() + MANUAL_MARGIN;
    c.playing = true;
    markCursor(true);
    resetSkipped();
    setPlaying(true);
    frame();
  }, [frame, resetSkipped, setSectionStart, markCursor]);

  // Départ à froid : tant que le contexte n'est pas actif et le piano pas décodé,
  // on n'ancre rien. Ancrer avant ferait défiler la grille en silence, et les
  // premiers pas seraient sautés pour retard. Piano déjà prêt : départ synchrone,
  // sans promesse ni tick manqué.
  const play = useCallback(() => {
    const l = lessonRef.current;
    if (!hasSteps(l)) return;
    if (clockRef.current.playing || pendingPlayRef.current) return;
    if (isPianoReady() && contextRunning()) {
      setPianoState('ready');
      startClock();
      return;
    }
    const gen = (playGenRef.current += 1);
    pendingPlayRef.current = true;
    setPendingPlay(true);
    setPianoState('loading');
    (async () => {
      try {
        await startAudioContext();
        await pianoReady({ timeoutMs: AUDIO_TIMEOUT_MS });
      } catch (e) {
        if (playGenRef.current !== gen) return;
        pendingPlayRef.current = false;
        setPendingPlay(false);
        // Récupérable : le play suivant relance le chargement.
        setPianoState('error');
        return;
      }
      if (playGenRef.current !== gen) return;
      pendingPlayRef.current = false;
      setPendingPlay(false);
      setPianoState('ready');
      startClock();
    })();
  }, [startClock]);

  const pause = useCallback(() => {
    pauseClock();
  }, [pauseClock]);

  // Un appui pendant l'attente du piano annule l'attente : le bouton ne reste
  // jamais mort, l'indicateur s'éteint et rien ne démarrera après coup.
  const toggle = useCallback(() => {
    if (clockRef.current.playing || pendingPlayRef.current) pause();
    else play();
  }, [pause, play]);

  const playStepNow = useCallback((step) => {
    Tone.start().catch(() => {});
    const secondsPerBeat = 60 / tempoRef.current;
    try {
      const now = audioTime();
      const when = now + MANUAL_MARGIN;
      traceSchedule({
        stepIndex: stepIdxRef.current,
        musicalTime: step.time,
        when,
        audioNow: now,
        bps: 1 / secondsPerBeat,
        mode: 'manual'
      });
      // Geste manuel : attaque et relâche posées d'un coup, comme un
      // triggerAttackRelease. Non annulable, et c'est voulu : une note immédiate
      // n'a rien à annuler.
      const names = step.notes.map((n) => noteName(n.midi));
      if (attackLessonNotes(names, when)) {
        step.notes.forEach((n, i) => {
          releaseLessonNotes(
            [names[i]],
            when + Math.max(n.duration * secondsPerBeat, MIN_STEP_DURATION)
          );
        });
      }
    } catch (e) {
      // le sampler peut jeter tant que ses buffers ne sont pas chargés
    }
  }, []);

  const step = useCallback((dir) => {
    const l = lessonRef.current;
    if (!hasSteps(l)) return;
    pauseClock();
    const idx = stepIdxRef.current;
    const range = sectionRange(l, idx);
    if (atSectionStartRef.current) {
      if (dir > 0) {
        setSectionStart(false);
        seek(l.steps[idx].time);
        playStepNow(l.steps[idx]);
        return;
      }
      const sections = Array.isArray(l.sections) ? l.sections : [];
      const previous = sections[(l.steps[idx].sectionIndex ?? 0) - 1];
      if (!previous) return;
      const target = clamp(previous.firstStep, 0, l.steps.length - 1);
      seek(l.steps[target].time);
      setCurrentStep(target);
      return;
    }
    if (dir < 0 && idx === range.first) {
      setSectionStart(true);
      seek(l.steps[idx].time);
      return;
    }
    const next = clamp(idx + dir, 0, l.steps.length - 1);
    seek(l.steps[next].time);
    setCurrentStep(next);
    playStepNow(l.steps[next]);
  }, [pauseClock, seek, setCurrentStep, playStepNow, setSectionStart]);

  const stepForward = useCallback(() => step(1), [step]);
  const stepBack = useCallback(() => step(-1), [step]);

  const stop = useCallback(() => {
    const l = lessonRef.current;
    if (!hasSteps(l)) return;
    pauseClock();
    const { first } = sectionRange(l, stepIdxRef.current);
    seek(l.steps[first].time);
    setCurrentStep(first);
    setSectionStart(true);
  }, [pauseClock, seek, setCurrentStep, setSectionStart]);

  const seekToStep = useCallback((index) => {
    const l = lessonRef.current;
    if (!hasSteps(l)) return;
    pauseClock();
    const target = clamp(index, 0, l.steps.length - 1);
    seek(l.steps[target].time);
    setCurrentStep(target);
    setSectionStart(false);
  }, [pauseClock, seek, setCurrentStep, setSectionStart]);

  const jumpToSection = useCallback((sectionIndex) => {
    const l = lessonRef.current;
    if (!hasSteps(l)) return;
    const section = Array.isArray(l.sections) ? l.sections[sectionIndex] : null;
    if (!section) return;
    const target = clamp(section.firstStep, 0, l.steps.length - 1);
    // Annuler avant le seek : `cancelScheduled` fait repartir le curseur après la
    // position, `seek` le remet sur le pas visé, qui doit bien être joué.
    if (clockRef.current.playing) cancelScheduled();
    seek(l.steps[target].time);
    setCurrentStep(target);
    if (clockRef.current.playing) {
      setSectionStart(false);
      frame();
    } else {
      setSectionStart(true);
    }
  }, [seek, setCurrentStep, frame, setSectionStart, cancelScheduled]);

  // Changer de vitesse en lecture : rien n'est annulé, le curseur ne bouge pas.
  // Les pas déjà soumis sonnent sur l'ancienne grille, les suivants sur la
  // nouvelle, sans trou ni doublon. Voir `retime`.
  const setSpeed = useCallback((value) => {
    if (!Number.isFinite(value) || value <= 0) return;
    retime(() => { speedRef.current = value; });
    setSpeedState(value);
  }, [retime]);

  // Le tempo passe par le même chemin que la vitesse : même grille, mêmes règles.
  useEffect(() => {
    retime(() => {
      tempoRef.current = tempo ?? lesson?.tempo ?? DEFAULT_TEMPO;
    });
  }, [tempo, lesson, retime]);

  const stepAtIndex = hasSteps(lesson) ? lesson.steps[currentStepIndex] ?? null : null;
  const currentStep = atSectionStart ? null : stepAtIndex;

  // Objet mémoïsé : son identité ne bouge que quand une de ses valeurs bouge,
  // sinon chaque rendu du lecteur réabonnerait ses effets au moteur.
  return useMemo(() => ({
    currentStepIndex: lesson ? currentStepIndex : -1,
    currentStep,
    atSectionStart,
    currentSectionIndex: stepAtIndex ? stepAtIndex.sectionIndex ?? -1 : -1,
    sections: Array.isArray(lesson?.sections) ? lesson.sections : [],
    playing,
    speed,
    pendingPlay,
    pianoState,
    getSkippedSteps,
    play,
    pause,
    stop,
    toggle,
    stepForward,
    stepBack,
    setSpeed,
    jumpToSection,
    seekToStep,
  }), [
    lesson,
    currentStepIndex,
    currentStep,
    atSectionStart,
    stepAtIndex,
    playing,
    speed,
    pendingPlay,
    pianoState,
    getSkippedSteps,
    play,
    pause,
    stop,
    toggle,
    stepForward,
    stepBack,
    setSpeed,
    jumpToSection,
    seekToStep,
  ]);
}
